// admin/src/pages/GatewayMonitoring/gatewayApi.js
'use strict';

import { request } from '../../utils/request';

const GATEWAYS_BASE_PATH = 'admin/gateways';

const DEFAULT_FETCH_CONFIG = {
  cache: 'no-store',
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
};

/**
 * Normalizes backend error objects into fixed, client-safe human-friendly messages.
 * Maps:
 * - 401 => Session expired / unauthorized
 * - 403 => Permission denied
 * - 404 => 'Gateway monitoring is not enabled on the server'
 * - 429 => Too many requests
 * - 503 => 'configuration error, contact an administrator'
 * Never displays raw server text.
 *
 * @param {any} err
 * @returns {string}
 */
export function extractGatewayErrorMessage(err) {
  if (!err) return 'An unexpected error occurred.';

  const status = err.status || err.statusCode || err.response?.status;
  const msg = String(err.message || err.error || err.response?.data?.error || err.response?.data?.message || '');

  if (status === 401 || msg.toLowerCase().includes('unauthorized') || msg.toLowerCase().includes('authentication required')) {
    return 'Your session has expired. Please log in again.';
  }

  if (
    status === 403 ||
    msg.toLowerCase().includes('missing required permission') ||
    msg.toLowerCase().includes('access denied') ||
    msg.toLowerCase().includes('forbidden')
  ) {
    return 'Access denied. You do not have permission to perform this action.';
  }

  if (status === 404 || msg.toLowerCase().includes('disabled') || msg.toLowerCase().includes('monitoring is not enabled')) {
    return 'Gateway monitoring is not enabled on the server';
  }

  if (status === 429 || msg.toLowerCase().includes('too many requests')) {
    return 'Too many requests. Please wait before trying again.';
  }

  if (status === 503 || msg.toLowerCase().includes('configuration error') || msg.toLowerCase().includes('unavailable')) {
    return 'configuration error, contact an administrator';
  }

  return 'Operation failed. Please try again.';
}

/**
 * List all registered gateways with computed status.
 * @param {AbortSignal} [signal]
 */
export async function listGateways(signal) {
  return request(GATEWAYS_BASE_PATH, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get detailed information for a specific gateway.
 * @param {string} id
 * @param {AbortSignal} [signal]
 */
export async function getGateway(id, signal) {
  return request(`${GATEWAYS_BASE_PATH}/${id}`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Rename a gateway.
 * @param {string} id
 * @param {Object} payload
 * @param {string} payload.name
 */
export async function updateGateway(id, { name }) {
  return request(`${GATEWAYS_BASE_PATH}/${id}`, {
    method: 'PATCH',
    body: { name },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Permanently revoke gateway credentials.
 * @param {string} id
 */
export async function revokeGateway(id) {
  return request(`${GATEWAYS_BASE_PATH}/${id}/revoke`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Delete a gateway record.
 * @param {string} id
 */
export async function deleteGateway(id) {
  return request(`${GATEWAYS_BASE_PATH}/${id}`, {
    method: 'DELETE',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Issue a new one-time enrollment token (returned once).
 * @param {Object} [payload]
 * @param {number} [payload.expiresInHours=24]
 */
export async function createEnrollmentToken({ expiresInHours = 24 } = {}) {
  return request(`${GATEWAYS_BASE_PATH}/enrollment-tokens`, {
    method: 'POST',
    body: { expiresInHours: Number(expiresInHours) || 24 },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List enrollment tokens (without secrets).
 * @param {AbortSignal} [signal]
 */
export async function listEnrollmentTokens(signal) {
  return request(`${GATEWAYS_BASE_PATH}/enrollment-tokens`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Revoke an active enrollment token.
 * @param {string} tokenId
 */
export async function revokeEnrollmentToken(tokenId) {
  return request(`${GATEWAYS_BASE_PATH}/enrollment-tokens/${tokenId}`, {
    method: 'DELETE',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Query cursor-paginated log and audit events.
 * @param {Object} [params]
 * @param {string} [params.gatewayId]
 * @param {string} [params.level]
 * @param {string} [params.event]
 * @param {string} [params.outcome]
 * @param {string} [params.requestId]
 * @param {string} [params.from]
 * @param {string} [params.to]
 * @param {number} [params.limit=50]
 * @param {string} [params.cursor]
 * @param {AbortSignal} [signal]
 */
export async function listEvents(params = {}, signal) {
  const query = {};
  if (params.gatewayId) query.gatewayId = params.gatewayId;
  if (params.level && params.level !== 'all') query.level = params.level;
  if (params.event) query.event = params.event;
  if (params.outcome && params.outcome !== 'all') query.outcome = params.outcome;
  if (params.requestId) query.requestId = params.requestId;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.limit) query.limit = params.limit;
  if (params.cursor) query.cursor = params.cursor;

  return request(`${GATEWAYS_BASE_PATH}/events`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Export events CSV (up to 10,000 rows).
 * @param {Object} [params]
 */
export async function exportEventsCsv(params = {}) {
  const query = {};
  if (params.gatewayId) query.gatewayId = params.gatewayId;
  if (params.level && params.level !== 'all') query.level = params.level;
  if (params.event) query.event = params.event;
  if (params.outcome && params.outcome !== 'all') query.outcome = params.outcome;
  if (params.requestId) query.requestId = params.requestId;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;

  return request(`${GATEWAYS_BASE_PATH}/events/export.csv`, {
    method: 'GET',
    query,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: {
      ...DEFAULT_FETCH_CONFIG.headers,
      Accept: 'text/csv',
    },
  });
}

/**
 * Get gateway monitoring retention and alert settings.
 * @param {AbortSignal} [signal]
 */
export async function getSettings(signal) {
  return request(`${GATEWAYS_BASE_PATH}/settings`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Update gateway monitoring retention and alert settings.
 * @param {Object} payload
 * @param {number} [payload.retentionDays]
 * @param {Object} [payload.alertThresholds]
 */
export async function updateSettings(payload) {
  return request(`${GATEWAYS_BASE_PATH}/settings`, {
    method: 'PUT',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get AD gateway sync status.
 * @param {AbortSignal} [signal]
 */
export async function getAdSyncStatus(signal) {
  return request(`${GATEWAYS_BASE_PATH}/ad-sync/status`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Start a new AD gateway sync run.
 * @param {Object} [payload]
 * @param {string} [payload.gatewayId]
 * @param {boolean} [payload.dryRun]
 */
export async function startAdSync(payload = {}) {
  return request(`${GATEWAYS_BASE_PATH}/ad-sync`, {
    method: 'POST',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List historical AD sync runs.
 * @param {Object} [params]
 * @param {AbortSignal} [signal]
 */
export async function listAdSyncRuns(params = {}, signal) {
  return request(`${GATEWAYS_BASE_PATH}/ad-sync/runs`, {
    method: 'GET',
    query: params,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get details of a single AD sync run.
 * @param {string} runId
 * @param {AbortSignal} [signal]
 */
export async function getAdSyncRun(runId, signal) {
  return request(`${GATEWAYS_BASE_PATH}/ad-sync/runs/${runId}`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List paginated items of a single AD sync run.
 * @param {string} runId
 * @param {Object} [params]
 * @param {AbortSignal} [signal]
 */
export async function listAdSyncRunItems(runId, params = {}, signal) {
  return request(`${GATEWAYS_BASE_PATH}/ad-sync/runs/${runId}/items`, {
    method: 'GET',
    query: params,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get AD Auth Relay status for all eligible gateways.
 * @param {AbortSignal} [signal]
 */
export async function getAuthRelayStatus(signal) {
  return request(`${GATEWAYS_BASE_PATH}/auth-relay/status`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Approve a gateway's registered relay public key by verifying fingerprint.
 * @param {string} gatewayId
 * @param {string} fingerprint
 */
export async function approveAuthRelayKey(gatewayId, fingerprint) {
  return request(`${GATEWAYS_BASE_PATH}/auth-relay/${gatewayId}/approve`, {
    method: 'POST',
    body: { fingerprint },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Revoke a gateway's relay public key.
 * @param {string} gatewayId
 */
export async function revokeAuthRelayKey(gatewayId) {
  return request(`${GATEWAYS_BASE_PATH}/auth-relay/${gatewayId}/revoke`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Set gateway HA role (e.g. promote secondary to primary).
 * @param {string} id
 * @param {Object} payload
 * @param {string} payload.role - 'primary' or 'secondary'
 */
export async function setGatewayRole(id, { role }) {
  return request(`${GATEWAYS_BASE_PATH}/${id}/role`, {
    method: 'PATCH',
    body: { role },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

